import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { parseMoney } from '../../api/parseMoney'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { AccountsError } from './useAccounts'

/**
 * `GET /transactions/summary` — M5 Ticket 4 (#425 D1). The detail hero's
 * "In {month}" reads `netCents` alone; `total_income`/`total_expenses`/
 * `by_category` have no caller on this screen and are not carried here —
 * add them if a future caller needs them, rather than shipping unread fields.
 */
export type AccountSummary = { netCents: number }

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * `YYYY-MM-DD` in the VIEWER's own local calendar, never `Date#toISOString`
 * (which answers in UTC) — for anyone west of UTC, a render near local
 * midnight would otherwise send a boundary date that is still "yesterday"
 * server-side, silently dropping or double-counting that day's transactions
 * from a "this month" query.
 */
function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

const MONTH_NAME = new Intl.DateTimeFormat('en-CA', { month: 'long' })

/**
 * Month-to-date bounds for the hero's "In {month}" stat: the first of the
 * current calendar month through today, both in the viewer's local time.
 *
 * A plain function, not folded into the hook — `AccountHero.tsx` needs the
 * month NAME immediately, on every render, with no network round trip: it is
 * a fact about the wall clock, not about the query's own resolution, and
 * tying it to `useAccountSummary`'s return would make the label wait on a
 * request that has nothing to do with what it says.
 */
export function currentMonthRange(now = new Date()): { start: string; end: string; label: string } {
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  return { start: isoDate(start), end: isoDate(now), label: MONTH_NAME.format(now) }
}

async function getAccountSummary(accountId: string, start: string, end: string): Promise<AccountSummary> {
  const { data, error, response } = await api.GET('/api/v1/transactions/summary', {
    params: { query: { account_id: accountId, start_date: start, end_date: end } },
  })
  if (error !== undefined || data === undefined) throw new AccountsError(error, response.status)
  return { netCents: parseMoney(data.net) }
}

/**
 * `/transactions/summary`, scoped to one account and a date range — M5
 * Ticket 4, #425 D1.
 *
 * **`net` is read exactly as the wire sends it, never re-signed.** D1's own
 * retraction: a mid-review report claimed this endpoint's convention was
 * inverted relative to the mappers, and it was wrong — there are THREE sign
 * flips on the write path (`plaid_mapper.py:47` negates Plaid's outflow-
 * positive; `NormalizedTransaction.amount` is inflow-positive by DESIGN as a
 * DTO convention; `_to_storage_amount()` negates again into expense-positive
 * STORAGE), and expense-positive storage is exactly what this endpoint's
 * `net` assumes. `signedCents`/`totalsOf` in `accountMoney.ts` apply a
 * DIFFERENT flip, for a DIFFERENT reason (liabilities are positive-owed on
 * the wire and this app displays them negative) — that has no bearing on a
 * transaction net, and applying it here would be inventing a fourth flip on
 * top of three that already land the value correctly.
 *
 * **Two caveats this hook does not encode, because they are about WHERE the
 * figure is shown, not how it is fetched** (#425 D1): one account per call
 * (fine — this screen is about exactly one), and the result excludes
 * pending/excluded/inactive-connection transactions, so **`netCents` will
 * NOT reconcile against a raw transaction list** — a sum over
 * `useAccountTransactions.ts`'s own rows for the same month will disagree
 * with this figure by exactly those excluded rows.
 *
 * `AccountHero.tsx` used to carry that caveat as a visible line on the tile;
 * removed by user decision (design gap-fill round 4, 2026-09-17) — it hedged
 * a comparison nobody could make on that tile, since nothing there lists
 * transactions. **The fact still matters and belongs here instead**:
 * whoever gives `AccountTransactionsPanel` (`AccountDetail.tsx`) a "See
 * all" link to a real, filterable transaction list (M6) is the one who
 * needs to know this figure will not reconcile against it, and should
 * surface the caveat at THAT list, not back on this hook.
 *
 * `live` in `surfaces.ts` — `TransactionSummary` carries no `last_synced_at`,
 * the same reasoning `merchant-transactions` already established. No
 * `isEmpty`: a summary always has a shape, zeros for a quiet month rather
 * than an absent page of rows.
 */
export function useAccountSummary(
  accountId: string,
  start: string,
  end: string,
): AsyncState<AccountSummary> {
  const query = useQuery({
    queryKey: keys.transactions.summary(accountId, start, end),
    queryFn: () => getAccountSummary(accountId, start, end),
  })
  return fromQuery(query, {
    message: () => 'This month’s activity could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
  })
}
